// Retensi & Pembersihan Foto Absensi — halaman BARU (brief bos C3, 21 Agu 2026,
// §25 memory/KEPUTUSAN-TERTUNDA.md: "buatkan saja role-nya, biar saya tentukan
// sendiri"). Backend-nya (`routers/attendance.py`: GET/PUT/POST `/attendance/foto/*`)
// sudah lama ada tapi TIDAK PERNAH punya halaman — satu-satunya cara memakainya
// dulu adalah lewat API langsung, sama saja tidak ada fiturnya untuk siapa pun
// selain superadmin/admin yang berani curl manual.
//
// Digerbangi kunci izin `attendance` yang SUDAH ADA (bukan modul baru):
// - "view"   → buka halaman, lihat pratinjau (scoped: own/cabang/all, SAMA
//              persis modul yang membatasi GET /attendance & halaman Absensi).
// - "update" → ubah berapa hari retensi.
// - "delete" → jalankan pembersihan manual SEKARANG.
//
// Tombol "Hapus Sekarang" SENGAJA disembunyikan (bukan ditampilkan lalu 403,
// lihat CLAUDE.md aturan #3) untuk DUA kondisi yang backend tolak: (a) tidak
// punya attendance.delete, dan (b) lingkup datanya "Data Sendiri" — backend
// menolak lingkup itu total (menghapus bukti absensi sendiri = konflik
// kepentingan, lihat docstring `bersihkan_foto_sekarang` di backend) — jadi
// tombolnya tidak pernah bisa berhasil untuk lingkup itu, tampilkan penjelasan
// saja, bukan tombol yang ujungnya selalu gagal.
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { formatTanggal } from "@/lib/tanggal";
import {
  ArrowLeft, Camera, Save, Trash2, ShieldAlert, Info, AlertTriangle, Building2,
} from "lucide-react";

const LINGKUP_LABEL = {
  own: "Data Sendiri",
  cabang: "Hanya Cabang Saya",
  all: "Semua Cabang",
};

function Kartu({ icon: Icon, warna = "emerald", judul, sub, children, testId }) {
  return (
    <section className="bg-white border border-slate-200 rounded-xl shadow-sm p-5" data-testid={testId}>
      <div className="flex items-center gap-3 mb-4">
        <div className={`w-10 h-10 rounded-lg bg-${warna}-50 text-${warna}-600 grid place-items-center shrink-0`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <h2 className="font-display font-bold text-base text-slate-900">{judul}</h2>
          {sub && <p className="text-xs text-slate-500 mt-0.5">{sub}</p>}
        </div>
      </div>
      {children}
    </section>
  );
}

export default function AttendanceFotoRetensi() {
  const { can } = useAuth();
  const canView = can("attendance", "view");
  const canUpdate = can("attendance", "update");
  const canDelete = can("attendance", "delete");

  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [hariInput, setHariInput] = useState("");
  const [busySimpanHari, setBusySimpanHari] = useState(false);
  const [busyHapus, setBusyHapus] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/attendance/foto/status");
      setStatus(r.data);
      setHariInput(String(r.data.simpan_hari ?? ""));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Gagal memuat status foto absensi");
    } finally { setLoading(false); }
  };
  useEffect(() => { if (canView) load(); else setLoading(false); }, [canView]); // eslint-disable-line react-hooks/exhaustive-deps

  const simpanRetensi = async () => {
    const n = Number(hariInput);
    if (!Number.isFinite(n) || n <= 0) { toast.error("Jumlah hari retensi harus lebih dari 0"); return; }
    setBusySimpanHari(true);
    try {
      const r = await api.put("/attendance/foto/pengaturan", { foto_absensi_simpan_hari: n });
      toast.success(
        r.data.efektif_hari !== r.data.foto_absensi_simpan_hari
          ? `Tersimpan. Nilai efektif dibulatkan ke pagar bawah ${r.data.efektif_hari} hari.`
          : "Retensi foto absensi tersimpan."
      );
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan retensi"); }
    finally { setBusySimpanHari(false); }
  };

  // Cabang mana yang ada di pratinjau — dipakai untuk kalimat konfirmasi &
  // baris "per cabang". Kunci "(tanpa cabang)" datang dari backend untuk
  // baris absensi yang memang tidak punya `branch` terisi.
  const perCabangEntries = Object.entries(status?.per_cabang || {});

  const labelLingkupUntukPesan = () => {
    if (!status) return "";
    if (status.lingkup === "cabang") {
      const nama = perCabangEntries[0]?.[0];
      return nama ? ` cabang ${nama}` : "";
    }
    if (status.lingkup === "all") return " seluruh cabang";
    return "";
  };

  // Tombol Hapus TIDAK ditampilkan kalau backend PASTI akan menolaknya —
  // baik karena izin tidak ada, lingkup "own" (ditolak total, lihat komentar
  // atas berkas ini), maupun lingkup "cabang"/"own" yang identitasnya tidak
  // diketahui (backend balas 400, bukan menghapus semua secara diam-diam).
  const identitasTidakDiketahui = !!status?.lingkup_catatan;
  const bisaHapus = canDelete && status && status.lingkup !== "own" && !identitasTidakDiketahui;

  const hapusSekarang = async () => {
    if (!status) return;
    const pesan = `Hapus ${status.akan_dibersihkan} foto absensi${labelLingkupUntukPesan()} `
      + `sebelum ${formatTanggal(status.batas_tanggal)}? Tidak bisa dibatalkan.`;
    if (!window.confirm(pesan)) return;
    setBusyHapus(true);
    try {
      const r = await api.post("/attendance/foto/bersihkan-sekarang");
      toast.success(`${r.data.dihapus} foto absensi dibersihkan.`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal membersihkan foto"); }
    finally { setBusyHapus(false); }
  };

  if (!canView) {
    return (
      <div className="p-4 md:p-8">
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-10 text-center" data-testid="att-foto-tidak-punya-akses">
          <ShieldAlert className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          <p className="text-slate-700 font-medium">Anda tidak punya akses ke halaman ini</p>
          <p className="text-sm text-slate-500 mt-1">Hubungi admin untuk meminta izin modul "Absensi".</p>
        </div>
      </div>
    );
  }

  if (loading) return <div className="p-8 text-sm text-slate-500" data-testid="att-foto-loading">Memuat...</div>;

  return (
    <div className="p-4 md:p-8 max-w-3xl space-y-6" data-testid="attendance-foto-retensi">
      <header>
        <Link to="/attendance" className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-emerald-600 mb-2" data-testid="att-foto-kembali">
          <ArrowLeft className="w-3.5 h-3.5" /> Kembali ke Absensi
        </Link>
        <p className="text-[11px] text-slate-500 uppercase tracking-wider font-semibold flex items-center gap-1.5">
          <Camera className="w-3.5 h-3.5" /> Absensi
        </p>
        <h1 className="font-display text-2xl md:text-3xl font-bold text-slate-900 mt-1">Retensi &amp; Pembersihan Foto Absensi</h1>
        <p className="text-sm text-slate-600 mt-1 max-w-2xl">
          Foto selfie absensi disimpan langsung di database dan lama-lama membengkakkan penyimpanan.
          Halaman ini mengatur berapa hari foto disimpan sebelum otomatis dibersihkan, dan bisa juga
          menjalankan pembersihan itu sekarang juga. Jam masuk/pulang, keterlambatan, dan koordinat
          TIDAK ikut terhapus — hanya gambarnya.
        </p>
      </header>

      {status?.lingkup_catatan && (
        <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800 flex items-start gap-2" data-testid="att-foto-lingkup-catatan">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{status.lingkup_catatan}</span>
        </div>
      )}

      {status?.lingkup === "own" && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 flex items-start gap-2" data-testid="att-foto-own-blocked">
          <Info className="w-4 h-4 shrink-0 mt-0.5" />
          <span>
            Lingkup data Absensi akun Anda adalah <b>"Data Sendiri"</b> — pembersihan foto tidak tersedia
            untuk lingkup ini, karena berarti menghapus bukti kehadiran terhadap diri sendiri. Minta admin
            mengubah lingkup akun Anda ke "Hanya Cabang Saya" atau "Semua Cabang" di User Management kalau
            memang perlu menjalankan pembersihan ini.
          </span>
        </div>
      )}

      {/* ============ 1. Retensi ============ */}
      <Kartu icon={Camera} judul="Berapa Lama Foto Disimpan" testId="kartu-retensi-foto"
             sub="Foto lebih tua dari batas ini akan ikut dibersihkan (terjadwal tiap tanggal 1, atau manual di bawah).">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1" htmlFor="att-foto-hari-input">Jumlah hari retensi</label>
            <input
              id="att-foto-hari-input"
              type="number"
              min={1}
              value={hariInput}
              disabled={!canUpdate}
              onChange={(e) => setHariInput(e.target.value)}
              data-testid="att-foto-hari-input"
              className="h-9 w-28 px-3 rounded-lg border border-slate-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:bg-slate-50 disabled:text-slate-400"
            />
          </div>
          {canUpdate && (
            <button onClick={simpanRetensi} disabled={busySimpanHari}
                    data-testid="att-foto-hari-simpan"
                    className="h-9 px-4 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
              <Save className="w-3.5 h-3.5" /> {busySimpanHari ? "Menyimpan..." : "Simpan"}
            </button>
          )}
          <p className="text-[11px] text-slate-500 basis-full">
            Minimal 7 hari — di bawah itu HR tidak sempat memverifikasi absensi mencurigakan sebelum
            buktinya hilang. Jadwal otomatis: {status?.jadwal}.
          </p>
        </div>
        {!canUpdate && (
          <p className="text-[11px] text-slate-400 mt-2">Anda hanya bisa melihat pengaturan ini.</p>
        )}
      </Kartu>

      {/* ============ 2. Pratinjau ============ */}
      <Kartu icon={AlertTriangle} warna="amber" judul="Pratinjau Pembersihan" testId="kartu-pratinjau-foto"
             sub={`Lingkup data Anda untuk modul Absensi: "${LINGKUP_LABEL[status?.lingkup] || status?.lingkup || "?"}".`}>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
          <div className="rounded-lg border border-slate-200 p-3" data-testid="att-foto-stat-berfoto">
            <p className="text-[11px] text-slate-500">Catatan berfoto</p>
            <p className="text-xl font-bold text-slate-900">{status?.catatan_berfoto ?? "—"}</p>
          </div>
          <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-3" data-testid="att-foto-stat-akan-hilang">
            <p className="text-[11px] text-amber-700">Akan dibersihkan</p>
            <p className="text-xl font-bold text-amber-700">{status?.akan_dibersihkan ?? "—"}</p>
          </div>
          <div className="rounded-lg border border-slate-200 p-3" data-testid="att-foto-stat-batas">
            <p className="text-[11px] text-slate-500">Batas tanggal</p>
            <p className="text-sm font-semibold text-slate-900 mt-1">
              {status?.batas_tanggal ? formatTanggal(status.batas_tanggal) : "—"}
            </p>
          </div>
        </div>

        {status?.rentang_tanggal && (
          <p className="text-xs text-slate-600 mb-3" data-testid="att-foto-rentang-tanggal">
            Rentang tanggal yang akan dibersihkan: <b>{formatTanggal(status.rentang_tanggal.dari)}</b>
            {" "}s/d <b>{formatTanggal(status.rentang_tanggal.sampai)}</b>.
          </p>
        )}

        {perCabangEntries.length > 0 && (
          <div className="mb-4" data-testid="att-foto-per-cabang">
            <p className="text-xs font-semibold text-slate-700 mb-1.5 flex items-center gap-1">
              <Building2 className="w-3.5 h-3.5" /> Rincian per cabang
            </p>
            <div className="border border-slate-200 rounded-lg overflow-hidden">
              {perCabangEntries.map(([cabang, jumlah]) => (
                <div key={cabang} className="flex items-center justify-between px-3 py-1.5 text-xs border-b border-slate-100 last:border-b-0">
                  <span className="text-slate-700">{cabang}</span>
                  <span className="font-semibold text-slate-900">{jumlah}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        <p className="text-[11px] text-slate-500">{status?.catatan}</p>
      </Kartu>

      {/* ============ 3. Hapus sekarang ============ */}
      {bisaHapus && (
        <Kartu icon={Trash2} warna="rose" judul="Bersihkan Sekarang" testId="kartu-hapus-foto"
               sub="Menjalankan pembersihan tanpa menunggu jadwal otomatis tanggal 1. Permanen, tidak bisa dibatalkan.">
          <button onClick={hapusSekarang} disabled={busyHapus || !status?.akan_dibersihkan}
                  data-testid="att-foto-hapus-sekarang"
                  className="h-10 px-4 rounded-lg bg-rose-600 hover:bg-rose-700 text-white text-sm font-medium inline-flex items-center gap-2 disabled:opacity-50">
            <Trash2 className="w-4 h-4" />
            {busyHapus ? "Membersihkan..." : `Hapus ${status?.akan_dibersihkan || 0} Foto Sekarang`}
          </button>
          {!status?.akan_dibersihkan && (
            <p className="text-[11px] text-slate-400 mt-2">Tidak ada foto yang melewati batas retensi saat ini.</p>
          )}
        </Kartu>
      )}
    </div>
  );
}
